/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, withAssign } from '@reatom/framework'

import { pipe, Record, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network/atoms'
import { Network } from '@root/entities/network/model'
import { UnknownError } from '@root/shared'
import { ApiError } from '@root/shared/api'
import { reatomResource } from '@root/shared/lib'

import { fetchUserProfileRequestAction } from './profile-requests'
import { userProfileModelAtom } from './reatom-user-profile'

import type { ReferralCode } from '@root/entities/referral'
import type { InviteCode } from '@root/entities/referral/model'

/**
 * Current user profile
 */
const reatomProfile = (network: Network.Network) => {
  const stateAtom = reatomResource(
    async (ctx) => {
      const isLoggedIn = ctx.spy(isLoggedInAtom)
      if (!isLoggedIn) {
        return Result.fail(UnknownError.fromUnknown('Not loggedIn'))
      }
      return ctx.schedule(() =>
        fetchUserProfileRequestAction(ctx, { network: network }),
      )
    },
    {
      retry: {
        enabled: true,
        retryOn: (error) => !ApiError.isUnauthorizedOrExpired(error),
      },
    },
  )

  const setReferralCodeAction = action((ctx, referralCode: ReferralCode) => {
    const state = ctx.get(stateAtom)

    if (state.value) {
      return stateAtom.setSuccess(ctx, {
        ...state.value,
        referralInfo: pipe(
          state.value.referralInfo,
          Record.set(network, {
            ...state.value.referralInfo[network],
            code: referralCode,
          }),
        ),
      })
    }
  })

  return stateAtom.pipe(
    withAssign(() => ({
      setReferralCode: setReferralCodeAction,
    })),
  )
}

export const refreshProfileAction = action((ctx) => {
  const network = ctx.get(selectedNetworkAtom)
  const model = byNetwork[network]

  return model.refresh(ctx)
}, 'refreshProfileAction')

export const byNetwork = Network.byNetworkRecord((network) =>
  reatomProfile(network),
)

export const profileModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  return byNetwork[network]
}, 'profileStateAtom')

export const profileStateAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const state = ctx.spy(byNetwork[network])
  return state
}, 'profileStateAtom')

export const profileAtom = atom(
  (ctx) => ctx.spy(profileStateAtom).value,
  'profileAtom',
)

export const tradingProfitUsdAtom = atom((ctx) => {
  const profile = ctx.spy(profileAtom)
  return profile?.trading.profit.usd
}, 'tradingProfitUsdAtom')

export const currentJoinedReferralCodesAtom = atom<
  Array<InviteCode> | undefined
>((ctx) => {
  const profile = ctx.spy(profileAtom)
  if (profile) {
    return Network.toArray(profile.referralInfo, (x) => x.inviteCode).filter(
      Boolean,
    )
  }
  return undefined
}, 'currentJoinedReferralCodeAtom')

export const currentUserIsRefreshingAtom = atom((ctx) => {
  const model = ctx.spy(profileModelAtom)
  return ctx.spy(model.isRefreshing)
}, 'currentUserIsRefreshingAtom')

export const otherUserIsRefreshingAtom = atom((ctx) => {
  const model = ctx.spy(userProfileModelAtom)
  if (!model) {
    return false
  }
  return ctx.spy(model.isRefreshing)
}, 'otherUserIsRefreshingAtom')
